import { devLog } from '../../../core/utils/logging.js';

/**
 * Recursively collects all text nodes within a given node.
 * @param node - The node to collect text nodes from.
 * @param textNodes - The array to store collected text nodes.
 * @returns An array with all text nodes.
 */
function collectTextNodes(node: Node, textNodes: Node[] = []): Node[] {
  if (node.nodeType === Node.TEXT_NODE) {
    textNodes.push(node);
  } else {
    for (const child of Array.from(node.childNodes)) {
      collectTextNodes(child, textNodes);
    }
  }

  return textNodes;
}

/**
 * Gets all text ranges for the given search terms.
 * @param element - The element to search.
 * @param terms - The terms to highlight.
 * @param caseSensitive - Whether the search is case-sensitive.
 * @returns An array of ranges matching the search terms.
 */
function getTextRangeForHighlight(
  element: HTMLElement,
  terms: string[],
  caseSensitive: boolean,
): Range[] {
  const textNodes: Node[] = collectTextNodes(element);

  // Collect ranges for all search terms
  const ranges: Range[] = [];

  for (const textNode of textNodes) {
    const textContent = textNode.textContent ?? '';
    // Normalize text content if case sensitivity is disabled
    const searchText = caseSensitive
      ? textContent
      : textContent.toLocaleLowerCase();

    for (const term of terms) {
      // Normalize search term if case sensitivity is disabled
      const searchTerm = caseSensitive ? term : term.toLocaleLowerCase();
      let index = 0;

      while ((index = searchText.indexOf(searchTerm, index)) !== -1) {
        const range = new Range();
        range.setStart(textNode, index);
        range.setEnd(textNode, index + term.length);
        ranges.push(range);
        index += term.length;
      }
    }
  }

  return ranges;
}

/**
 * Creates a local range array that is added to the highlight and returns it.
 * @param targetElement - The element to search within.
 * @param terms - The terms to highlight.
 * @param caseSensitive - Whether the search is case-sensitive.
 * @param highlight - The Highlight instance to add the ranges to.
 * @returns An array of ranges that were added to the highlight.
 * @internal
 */
export function createRangedHighlights(
  targetElement: HTMLElement,
  terms: string[],
  caseSensitive: boolean,
  highlight: Set<AbstractRange> | Highlight,
): Range[] {
  const storedRanges: Range[] = [];

  if (terms.length === 0) {
    return storedRanges;
  }

  const ranges = getTextRangeForHighlight(targetElement, terms, caseSensitive);

  for (const range of ranges) {
    try {
      highlight.add(range);
      storedRanges.push(range);
    } catch {
      devLog('Failed to add range', range);
    }
  }

  return storedRanges;
}
